import axios from "axios";
import { doc, updateDoc, deleteField, getDoc } from "firebase/firestore";
import {
  PLATFORM_SERVICES_COLLECTION,
  USER_DATA_AND_CHATBOT_COLLECTION,
} from "constants/global/firebase-collections";
import { storeProfileImage, sweetAlert } from "./global";
import { ApiServerPath } from "api_server";
import {
  getAccountLocks,
  getSocialLockRejectionMessage,
  isSocialIdentityAllowed,
} from "./accountLocks";

type YouTubeServiceKey = "googleSocial" | "googleAdwords";

const removeGoogleSocialConnection = async (workspaceId: string) => {
  try {
    const ref = doc(PLATFORM_SERVICES_COLLECTION, workspaceId);
    await updateDoc(ref, { googleSocial: deleteField() });
  } catch (cleanupError) {
    console.log("YouTube social cleanup failed:", cleanupError);
  }
};

/**
 * Enforce YouTube social identity lock. When rejected, removes the
 * prematurely-saved googleSocial tokens and alerts the user.
 * Lock *check* failures stay non-blocking; a rejected identity must
 * always abort — even if cleanup updateDoc fails.
 *
 * @returns false when the identity is locked out; true otherwise
 */
const enforceYouTubeSocialLock = async (
  workspaceId: string,
  channelId?: string | null,
): Promise<boolean> => {
  try {
    const userDataSnap = await getDoc(
      doc(USER_DATA_AND_CHATBOT_COLLECTION, workspaceId),
    );
    const locks = getAccountLocks(userDataSnap.data() || {});
    if (
      !isSocialIdentityAllowed(locks, "youtube", {
        channelId: channelId || undefined,
      })
    ) {
      sweetAlert(getSocialLockRejectionMessage(locks, "youtube"), "error");
      await removeGoogleSocialConnection(workspaceId);
      return false;
    }
  } catch (lockError) {
    console.log("YouTube lock check failed (non-blocking):", lockError);
  }
  return true;
};

/**
 * Updates YouTube channel info (name + profile image) in Firestore.
 * Prefer `googleSocial` for social posting; `googleAdwords` kept for legacy.
 *
 * For googleSocial, `updatePlatforms` may have already saved the refresh
 * token. If channel lookup fails (or the identity is locked), this removes
 * that incomplete connection so a new Google identity is not left connected.
 *
 * @returns true when the googleSocial connection was kept; false when it was
 *   removed or the legacy path finished without a social connection.
 */
export const updateYouTubeChannelInfo = async (
  accessToken: string,
  workspaceId: string,
  serviceKey: YouTubeServiceKey = "googleSocial",
): Promise<boolean> => {
  try {
    const { data } = await axios.post(
      `${ApiServerPath.API_SERVER_BASE_URL}${ApiServerPath.GET_YOUTUBE_CHANNEL_INFO}`,
      {
        accessToken,
      },
    );

    if (!data.success || !data.results?.success) {
      console.log(
        "No YouTube channel found for user:",
        data.results?.error || "Unknown error",
      );

      // Tokens were already saved by updatePlatforms. Without a channel we
      // cannot verify a locked identity — always remove the incomplete
      // googleSocial connection (lock rejection message when applicable).
      if (serviceKey === "googleSocial") {
        const allowed = await enforceYouTubeSocialLock(workspaceId, undefined);
        if (allowed) {
          sweetAlert(
            "No YouTube channel found for this Google account. Connect an account that has a YouTube channel.",
            "error",
          );
          await removeGoogleSocialConnection(workspaceId);
        }
        return false;
      }
      return false;
    }

    const { channelId, channelName, thumbnailUrl } = data.results;
    const ref = doc(PLATFORM_SERVICES_COLLECTION, workspaceId);

    if (serviceKey === "googleSocial") {
      if (!(await enforceYouTubeSocialLock(workspaceId, channelId))) {
        return false;
      }

      await updateDoc(ref, {
        "googleSocial.channelId": channelId || null,
        "googleSocial.channelName": channelName,
        "googleSocial.isConnected": true,
        "googleSocial.userName": channelName || null,
      });

      if (thumbnailUrl) {
        try {
          const permanentUrl = await storeProfileImage(thumbnailUrl, "google");
          if (permanentUrl) {
            await updateDoc(ref, {
              "googleSocial.profileImageUrl": permanentUrl,
            });
          }
        } catch (imageError) {
          console.log(
            "Failed to store YouTube profile image (non-critical):",
            imageError,
          );
        }
      }
      return true;
    }

    // Legacy path under googleAdwords
    await updateDoc(ref, {
      "googleAdwords.youtubeChannelName": channelName,
    });

    if (thumbnailUrl) {
      try {
        const permanentUrl = await storeProfileImage(thumbnailUrl, "google");
        if (permanentUrl) {
          await updateDoc(ref, {
            "googleAdwords.youtubeProfileImageUrl": permanentUrl,
          });
        }
      } catch (imageError) {
        console.log(
          "Failed to store YouTube profile image (non-critical):",
          imageError,
        );
      }
    }
    return false;
  } catch (error) {
    console.log("Failed to update YouTube channel info:", error);

    // Same as the no-channel path: do not leave a half-connected googleSocial
    // identity when channel lookup throws.
    if (serviceKey === "googleSocial") {
      const allowed = await enforceYouTubeSocialLock(workspaceId, undefined);
      if (allowed) {
        sweetAlert(
          "Failed to verify YouTube channel for this Google account. Please try connecting again.",
          "error",
        );
        await removeGoogleSocialConnection(workspaceId);
      }
    }
    return false;
  }
};
